/**
 * WebSocket client with auto-reconnect and Last-Event-Id replay.
 * Connects to the api-worker /ws endpoint, which upgrades through the
 * UserState DO Hibernatable WS. Buffered events (last 100, 1h TTL) are
 * replayed on reconnect.
 */
export interface WsClientOptions {
  url: string;
  onEvent: (evt: { id: number; ts: number; payload: unknown }) => void;
  onStateChange?: (state: 'connecting' | 'open' | 'closed' | 'error') => void;
}

export class WsClient {
  #ws: WebSocket | null = null;
  #lastEventId = -1;
  #closed = false;
  #backoffMs = 1000;
  readonly #opts: WsClientOptions;

  constructor(opts: WsClientOptions) {
    this.#opts = opts;
    this.#connect();
  }

  #connect(): void {
    if (this.#closed) return;
    this.#opts.onStateChange?.('connecting');
    const url = new URL(this.#opts.url);
    if (this.#lastEventId >= 0) {
      url.searchParams.set('last_event_id', String(this.#lastEventId));
    }
    const ws = new WebSocket(url.toString());
    this.#ws = ws;
    ws.addEventListener('open', () => {
      this.#backoffMs = 1000;
      this.#opts.onStateChange?.('open');
    });
    ws.addEventListener('message', (ev) => {
      try {
        const data = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as {
          id: number;
          ts: number;
          payload: unknown;
        };
        if (typeof data.id === 'number') this.#lastEventId = Math.max(this.#lastEventId, data.id);
        this.#opts.onEvent(data);
      } catch {
        /* ignore malformed frames */
      }
    });
    ws.addEventListener('close', () => {
      this.#opts.onStateChange?.('closed');
      this.#scheduleReconnect();
    });
    ws.addEventListener('error', () => {
      this.#opts.onStateChange?.('error');
      try {
        ws.close();
      } catch {
        /* already closed */
      }
    });
  }

  #scheduleReconnect(): void {
    if (this.#closed) return;
    const wait = Math.min(this.#backoffMs, 30_000);
    this.#backoffMs = Math.min(this.#backoffMs * 2, 30_000);
    setTimeout(() => this.#connect(), wait);
  }

  close(): void {
    this.#closed = true;
    this.#ws?.close();
  }
}
